import {
  AnyAction,
  EnhancedStore,
  Middleware,
  Reducer,
  Store,
  configureStore,
} from '@reduxjs/toolkit';
import { ThunkMiddlewareFor } from '@reduxjs/toolkit/src/getDefaultMiddleware';
import { persistStore } from 'redux-persist';
import { Persistor } from 'redux-persist/lib/types';
import {
  Config as StateSyncConfig,
  createStateSyncMiddleware,
} from 'redux-state-sync';
import { isDevelopOrSnapshot, isTesting } from 'vet-bones/utils';

import { ProjectsActionType } from 'src/actions/projects/actionTypes';
import {
  PersistedReducerReduxState,
  RESET_STORE,
  createInitialState,
  persistedReducer,
} from 'src/reducers/reducers';
import { cloudProjectMiddleware } from 'src/store/cloudProjectMiddleware';
import { actionSanitizer } from 'src/store/sanitize/action';
import { stateSanitizer } from 'src/store/sanitize/state';
import { ReduxState } from 'src/store/types';

const stateSyncConfig: StateSyncConfig = {
  // only the following actions are synced between tabs
  whitelist: [
    ProjectsActionType.ACCEPTED_PROJECT_DRAFT_DIAGRAM,
    ProjectsActionType.ADD_IMPORTED_MODELS_TO_PROJECT_FINISHED,
    ProjectsActionType.ADD_IMPORTED_MODELS_TO_PROJECT_STARTED,
    ProjectsActionType.ADDED_FILE_UPLOADS_TO_PROJECT,
    ProjectsActionType.ADDED_LOCAL_MODEL_TO_PROJECT,
    ProjectsActionType.ADDED_PROJECT,
    ProjectsActionType.ADDED_SUGGESTIONS_TO_PROJECT,
    ProjectsActionType.ADDED_VIRTUAL_GRAPHS_TO_PROJECT,
    ProjectsActionType.ARCHIVED_PROJECT,
    ProjectsActionType.DUPLICATED_LOCAL_PROJECT,
    ProjectsActionType.CLEARED_PROJECT_VOICEBOX_EVALUATION,
    ProjectsActionType.EVALUATED_PROJECT_COMPETENCY_QUESTIONS,
    ProjectsActionType.EVALUATED_PROJECT_SUMMARY,
    ProjectsActionType.IMPORTED_PROJECT_FROM_FILE,
    ProjectsActionType.OPENED_PROJECT_COMPETENCY_QUESTION,
    ProjectsActionType.PUBLISH_PROJECT_FINISHED,
    ProjectsActionType.REJECTED_PROJECT_DRAFT_DIAGRAM,
    ProjectsActionType.RENAMED_PROJECT,
    ProjectsActionType.REQUEST_PROJECT_MODEL_CLASSES_SUCCESS,
    ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_SUCCESS,
    ProjectsActionType.REQUEST_PROJECT_MODEL_PROPERTIES_SUCCESS,
    ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_SUCCESS,
    ProjectsActionType.CLEARED_PROJECT_VOICEBOX_EVALUATION,
    ProjectsActionType.RESTORED_LOCAL_PROJECT,
    ProjectsActionType.UPDATED_PROJECT_DATA_NAMESPACE,
    ProjectsActionType.UPDATED_PROJECT_DESCRIPTION,
    ProjectsActionType.UPDATED_PROJECT_DIAGRAM,
    ProjectsActionType.UPDATED_PROJECT_FILE_UPLOAD,
    ProjectsActionType.UPDATED_PROJECT_MODEL_NAME,
    ProjectsActionType.UPDATED_PROJECT_MODEL_NAMESPACE,
    ProjectsActionType.UPDATED_PROJECT_PROPERTY_TYPE,
    ProjectsActionType.UPDATED_PROJECT_TARGET_MODEL_ID,
    ProjectsActionType.UPDATED_PROJECT_VIRTUAL_GRAPH,
    ProjectsActionType.UPDATED_PROJECT_VOICEBOX_CONVERSATION,
    ProjectsActionType.UPDATED_PROJECT_VOICEBOX_SUGGESTIONS_ENDPOINT,
  ],
};

export const createStore = (
  preloadedState: ReduxState,
  reducer: Reducer<PersistedReducerReduxState>,
  extraMiddleware?: Middleware
): EnhancedStore<ReduxState, AnyAction, [ThunkMiddlewareFor<ReduxState>]> =>
  configureStore({
    devTools: isDevelopOrSnapshot()
      ? { actionSanitizer, actionsDenylist: [], stateSanitizer }
      : false,
    middleware: (getDefaultMiddleware) => {
      const middleware = [
        ...getDefaultMiddleware({
          immutableCheck: false, // Disabled because it's slower than the threshold (can maybe re-enable later or increase threshold)
          serializableCheck: false, // Disabled because our reducer state is typically initialized as a class (refactor?)
        }),
        createStateSyncMiddleware({
          ...stateSyncConfig,
          ...(isTesting()
            ? { broadcastChannelOption: { type: 'simulate' } }
            : {}),
        }),
        cloudProjectMiddleware,
      ];
      if (extraMiddleware) {
        middleware.push(extraMiddleware);
      }
      return middleware;
    },
    preloadedState,
    reducer,
  });

export type AppDispatch = ReturnType<typeof createStore>['dispatch'];

type NotSetup = typeof notSetup;
type GetStore = () => Store<ReduxState>;
type ResetStore = () => Promise<void>;
type GetPersistor = () => Persistor;

const notSetup = () => {
  throw new Error('Store cannot be accessed before calling `setupStore`!');
};
// These are overwritten below, after `setupStore` is called.
export let getStore: NotSetup | GetStore = notSetup;
export let resetStore: NotSetup | ResetStore = notSetup;
export let getPersistor: NotSetup | GetPersistor = notSetup;

export const setupStore = (
  store: Store<ReduxState> = createStore(
    createInitialState(),
    persistedReducer
  ),
  persistor: Persistor | null = persistStore(store)
) => {
  getStore = () => store;
  resetStore = async () => {
    store.dispatch({ type: RESET_STORE });
  };

  if (persistor) {
    getPersistor = () => persistor;
  }
};
